import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { format } from 'date-fns';
import { Loader, ChevronLeft, Tag, IndianRupee, ExternalLink, Edit2, Trash2, AlertCircle, Search, CircleDollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { CollectibleWithImages } from '../lib/supabase';
import DeleteConfirmModal from './DeleteConfirmModal';

function AdminCollectibles() {
  const navigate = useNavigate();
  const [collectibles, setCollectibles] = useState<CollectibleWithImages[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCollectible, setSelectedCollectible] = useState<CollectibleWithImages | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCollectibles, setFilteredCollectibles] = useState<CollectibleWithImages[]>([]);

  useEffect(() => {
    const checkAdminAndFetchCollectibles = async () => {
      try {
        // Check if user is admin
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        const { data: profile } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', user.id)
          .single();

        if (!profile?.is_admin) {
          throw new Error('Not authorized');
        }

        setIsAdmin(true);

        // Fetch collectibles with images
        const { data: collectiblesData, error: collectiblesError } = await supabase
          .from('collectibles')
          .select(`
            *,
            images:collectible_images (
              id,
              image_url,
              order
            ),
            category:categories (
              id,
              name
            )
          `)
          .order('created_at', { ascending: false });

        if (collectiblesError) throw collectiblesError;
        setCollectibles(collectiblesData || []);
        setFilteredCollectibles(collectiblesData || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load collectibles');
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchCollectibles();
  }, []);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredCollectibles(collectibles);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = collectibles.filter(item => 
      item.name.toLowerCase().includes(query) ||
      item.description?.toLowerCase().includes(query) ||
      item.type.toLowerCase().includes(query) ||
      item.price.toString().includes(query) ||
      item.category?.name.toLowerCase().includes(query)
    );

    setFilteredCollectibles(filtered);
  }, [searchQuery, collectibles]);

  const updateStatus = async (collectible: CollectibleWithImages, status: 'available' | 'sold' | 'unavailable') => {
    try {
      setUpdatingStatus(collectible.id);

      const { error: updateError } = await supabase
        .from('collectibles')
        .update({ status })
        .eq('id', collectible.id);

      if (updateError) throw updateError;

      setCollectibles(prev => prev.map(c => {
        if (c.id === collectible.id) {
          return { ...c, status };
        }
        return c;
      }));

      setFilteredCollectibles(prev => prev.map(c => {
        if (c.id === collectible.id) {
          return { ...c, status };
        }
        return c;
      }));
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async () => {
    if (!selectedCollectible) return;
    
    try {
      // Delete all images from storage
      if (selectedCollectible.images && selectedCollectible.images.length > 0) {
        const imagePaths = selectedCollectible.images.map(img => {
          const url = new URL(img.image_url);
          return url.pathname.split('/').slice(-2).join('/');
        });

        for (const path of imagePaths) {
          await supabase.storage
            .from('collectible-images')
            .remove([path]);
        }
      }

      // Delete the collectible (this will cascade delete images from the database)
      const { error: deleteError } = await supabase
        .from('collectibles')
        .delete()
        .eq('id', selectedCollectible.id);

      if (deleteError) throw deleteError;

      setCollectibles(prev => prev.filter(c => c.id !== selectedCollectible.id));
      setShowDeleteModal(false);
      setSelectedCollectible(null);
    } catch (err) {
      console.error('Error deleting collectible:', err);
      alert('Failed to delete collectible. Please try again.');
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to view this page.</p>
          <Link to="/" className="text-primary-600 hover:text-primary-800 font-medium">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Manage Collectibles - Admin</title>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5" />
              Back to Home
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">Manage Collectibles</h1>
          </div>
          <div className="bg-primary-100 text-primary-700 px-3 py-1 rounded-full text-sm font-medium">
            {filteredCollectibles.length} Items
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search collectibles by name, type, or price..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>

        {filteredCollectibles.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
            <CircleDollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchQuery ? 'No matching collectibles found' : 'No Collectibles Yet'}
            </h3>
            <p className="text-gray-600">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Add some collectibles to get started.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredCollectibles.map((collectible) => (
              <div
                key={collectible.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start gap-6">
                    <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                      {collectible.images?.[0] && (
                        <img
                          src={collectible.images[0].image_url}
                          alt={collectible.name}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>

                    <div className="flex-grow">
                      <div className="flex items-start justify-between gap-4 mb-4">
                        <div>
                          <Link
                            to={`/${collectible.slug}`}
                            className="text-lg font-medium text-gray-900 hover:text-primary-600 flex items-center gap-2"
                          >
                            {collectible.name}
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-sm text-gray-500">
                              {collectible.type.charAt(0).toUpperCase() + collectible.type.slice(1)}
                            </span>
                            <span className="text-sm text-gray-500">•</span>
                            <span className="text-sm text-gray-500">
                              {collectible.category?.name || 'Uncategorized'}
                            </span>
                            <span className="text-sm text-gray-500">•</span>
                            <time className="text-sm text-gray-500">
                              {format(new Date(collectible.created_at), 'PPp')}
                            </time>
                          </div>
                        </div>
                        <div className="text-xl font-bold text-gray-900 flex items-center gap-1">
                          <IndianRupee className="h-5 w-5" />
                          {collectible.price.toLocaleString()}
                        </div>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {collectible.status === 'sold' ? (
                            <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                              <Tag className="h-4 w-4" />
                              Sold
                            </span>
                          ) : collectible.status === 'unavailable' ? (
                            <span className="bg-gray-50 text-gray-600 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5">
                              <Tag className="h-4 w-4" />
                              Unavailable
                            </span>
                          ) : (
                            <span className="bg-green-50 text-green-600 px-3 py-1 rounded-full text-sm font-medium">
                              Available
                            </span>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {updatingStatus === collectible.id ? (
                            <Loader className="h-5 w-5 animate-spin text-primary-600" />
                          ) : (
                            <>
                              <div className="relative">
                                <select
                                  value={collectible.status}
                                  onChange={(e) => updateStatus(collectible, e.target.value as 'available' | 'sold' | 'unavailable')}
                                  className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-gray-300 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                                >
                                  <option value="available">Available</option>
                                  <option value="sold">Sold</option>
                                  <option value="unavailable">Unavailable</option>
                                </select>
                              </div>
                              <Link
                                to={`/edit/${collectible.id}`}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                              >
                                <Edit2 className="h-4 w-4" />
                                Edit
                              </Link>
                              <button
                                onClick={() => {
                                  setSelectedCollectible(collectible);
                                  setShowDeleteModal(true);
                                }}
                                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors flex items-center gap-2"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCollectible(null);
        }}
        onConfirm={handleDelete}
        title={selectedCollectible?.name || ''}
      />
    </>
  );
}

export default AdminCollectibles;